import React, { useState, useEffect } from 'react';
import ImageCropper from './ImageCropper';
import { API_URL, COLOR_PRESETS, COLORS } from '../config';
import { useNotification } from './NotificationSystem';
import './ServerSettings.css';

function ServerSettings({ server, onClose, token, onServerUpdate }) {
  const { showSuccess, showError, confirm, prompt } = useNotification();
  const [activeTab, setActiveTab] = useState('overview');
  const [serverName, setServerName] = useState(server.name);
  const [isEditing, setIsEditing] = useState(false);
  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showCreateRole, setShowCreateRole] = useState(false);
  const [newRole, setNewRole] = useState({ name: '', color: COLORS.PRIMARY, permissions: 0, gradient: null, holographic: false });
  const [editingRole, setEditingRole] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  useEffect(() => {
    if (activeTab === 'members') {
      fetchMembers();
      fetchRoles(); // Need roles for role assignment
    } else if (activeTab === 'roles') {
      fetchRoles();
    }
  }, [activeTab]);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/servers/${server.id}/members`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMembers(data);
      }
    } catch (error) {
      console.error('Error fetching members:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/servers/${server.id}/roles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRoles(data);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveServerName = async () => {
    try {
      const response = await fetch(`${API_URL}/api/servers/${server.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name: serverName })
      });
      
      if (response.ok) {
        const updated = await response.json();
        onServerUpdate(updated);
        setIsEditing(false);
      } else {
        showError('Failed to update server name');
      }
    } catch (error) {
      console.error('Error updating server name:', error);
      showError('Failed to update server name');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      showError('Please select a valid image file (JPEG, PNG, or WebP)');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      showError('Image size must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleCropComplete = async (croppedBlob) => {
    try {
      setUploadingAvatar(true);
      setImagePreview(null);

      const formData = new FormData();
      formData.append('avatar', croppedBlob, 'server-avatar.jpg');

      const response = await fetch(`${API_URL}/api/servers/${server.id}/avatar`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        showSuccess('Server avatar updated successfully!');
        // Update server with new avatar
        onServerUpdate({ ...server, avatar_url: result.avatar_url });
      } else {
        const error = await response.json();
        showError(error.error || 'Failed to upload server avatar');
      }
    } catch (error) {
      console.error('Error uploading server avatar:', error);
      showError('Failed to upload server avatar');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleCreateRole = async () => {
    try {
      const response = await fetch(`${API_URL}/api/servers/${server.id}/roles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newRole)
      });
      
      if (response.ok) {
        const role = await response.json();
        setRoles([...roles, role]);
        setShowCreateRole(false);
        setNewRole({ name: '', color: '#5865F2', permissions: 0 });
      } else {
        showError('Failed to create role');
      }
    } catch (error) {
      console.error('Error creating role:', error);
      showError('Failed to create role');
    }
  };

  const handleUpdateRole = async (roleId, updates) => {
    try {
      const response = await fetch(`${API_URL}/api/servers/${server.id}/roles/${roleId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });
      
      if (response.ok) {
        const updated = await response.json();
        setRoles(roles.map(r => r.id === roleId ? updated : r));
        setEditingRole(null);
      } else {
        showError('Failed to update role');
      }
    } catch (error) {
      console.error('Error updating role:', error);
      showError('Failed to update role');
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!await confirm('Are you sure you want to delete this role?', 'Delete Role')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/servers/${server.id}/roles/${roleId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setRoles(roles.filter(r => r.id !== roleId));
      } else {
        showError('Failed to delete role');
      }
    } catch (error) {
      console.error('Error deleting role:', error);
      showError('Failed to delete role');
    }
  };

  const handleAssignRole = async (userId, roleId) => {
    try {
      const response = await fetch(`${API_URL}/api/servers/${server.id}/members/${userId}/roles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ roleId })
      });
      
      if (response.ok) {
        // Refresh members to show updated roles
        fetchMembers();
      } else {
        showError('Failed to assign role');
      }
    } catch (error) {
      console.error('Error assigning role:', error);
      showError('Failed to assign role');
    }
  };

  const handleRemoveRole = async (userId, roleId) => {
    try {
      const response = await fetch(`${API_URL}/api/servers/${server.id}/members/${userId}/roles/${roleId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        // Refresh members to show updated roles
        fetchMembers();
      } else {
        showError('Failed to remove role');
      }
    } catch (error) {
      console.error('Error removing role:', error);
      showError('Failed to remove role');
    }
  };

  const handleDeleteServer = async () => {
    const confirmDelete = await confirm(
      `Are you absolutely sure you want to delete "${server.name}"?\n\n` +
      'This will:\n' +
      '• Permanently delete all channels and messages\n' +
      '• Remove all members from the server\n' +
      '• Delete all roles and settings\n' +
      '• Disconnect anyone currently in voice channels\n\n' +
      'This action CANNOT be undone!',
      'Delete Server'
    );

    if (!confirmDelete) return;

    const doubleConfirm = await prompt(
      `Type the server name "${server.name}" to confirm deletion:`,
      'Confirm Server Deletion'
    );

    if (doubleConfirm !== server.name) {
      showError('Server name does not match. Deletion cancelled.');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/servers/${server.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        showSuccess('Server deleted successfully');
        onClose(); // Close settings modal
        // The server_deleted socket event will handle removing it from the list
      } else {
        const error = await response.json();
        showError(error.error || 'Failed to delete server');
      }
    } catch (error) {
      console.error('Error deleting server:', error);
      showError('Failed to delete server');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const PERMISSIONS = {
    MANAGE_CHANNELS: 1,
    MANAGE_ROLES: 2,
    KICK_MEMBERS: 4,
    BAN_MEMBERS: 8,
    MANAGE_SERVER: 16,
  };

  const hasPermission = (permissions, permission) => {
    return (permissions & permission) === permission;
  };

  const togglePermission = (permissions, permission) => {
    if (hasPermission(permissions, permission)) {
      return permissions & ~permission;
    } else {
      return permissions | permission;
    }
  };

  return (
    <>
      {imagePreview && (
        <ImageCropper
          image={imagePreview}
          onCropComplete={handleCropComplete}
          onCancel={() => setImagePreview(null)}
          aspectRatio={1}
        />
      )}
      
      <div className="server-settings-backdrop" onClick={onClose}>
        <div className="server-settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="server-settings-sidebar">
          <div className="server-settings-tabs">
            <div 
              className={`settings-tab ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </div>
            <div 
              className={`settings-tab ${activeTab === 'members' ? 'active' : ''}`}
              onClick={() => setActiveTab('members')}
            >
              Members
            </div>
            <div 
              className={`settings-tab ${activeTab === 'roles' ? 'active' : ''}`}
              onClick={() => setActiveTab('roles')}
            >
              Roles
            </div>
          </div>
          <button className="close-settings-btn" onClick={onClose}>
            ✕ Close
          </button>
        </div>

        <div className="server-settings-content">
          {activeTab === 'overview' && (
            <div className="settings-section">
              <h2>Server Overview</h2>
              
              <div className="setting-item">
                <label>Server Icon</label>
                <div className="server-avatar-container">
                  {server.avatar_url ? (
                    <img 
                      src={`${API_URL}${server.avatar_url}`} 
                      alt="Server Icon" 
                      className="server-avatar-preview"
                    />
                  ) : (
                    <div className="server-avatar-preview">
                      {server.name[0]?.toUpperCase()}
                    </div>
                  )}
                  <div className="avatar-upload-section">
                    <label htmlFor="server-avatar-upload" className="server-avatar-upload-btn">
                      {uploadingAvatar ? 'Uploading...' : 'Change Icon'}
                    </label>
                    <input
                      id="server-avatar-upload"
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                      disabled={uploadingAvatar}
                    />
                    <p className="upload-hint">Recommended size: 512x512px</p>
                  </div>
                </div>
              </div>

              <div className="setting-item">
                <label>Server Name</label>
                {isEditing ? (
                  <div className="edit-server-name">
                    <input
                      type="text"
                      value={serverName}
                      onChange={(e) => setServerName(e.target.value)}
                      className="server-name-input"
                      autoFocus
                    />
                    <div className="edit-actions">
                      <button onClick={handleSaveServerName} className="btn-save">Save</button>
                      <button onClick={() => {
                        setServerName(server.name);
                        setIsEditing(false);
                      }} className="btn-cancel">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="server-name-display">
                    <span>{serverName}</span>
                    <button onClick={() => setIsEditing(true)} className="btn-edit">Edit</button>
                  </div>
                )}
              </div>
              <div className="setting-item">
                <label>Server ID</label>
                <div className="server-id">{server.id}</div>
              </div>

              <div className="danger-zone">
                <h3 className="danger-zone-title">Danger Zone</h3>
                <div className="danger-zone-content">
                  <div className="danger-zone-text">
                    <strong>Delete Server</strong>
                    <p>Permanently delete this server and all of its content. This action cannot be undone.</p>
                  </div>
                  <button 
                    className="btn-delete-server" 
                    onClick={handleDeleteServer}
                  >
                    Delete Server
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'members' && (
            <div className="settings-section">
              <h2>Members — {members.length}</h2>
              {loading ? (
                <div className="loading">Loading members...</div>
              ) : (
                <div className="members-list">
                  {members.map(member => (
                    <div key={member.user_id} className="member-item">
                      <div className="member-info">
                        <div 
                          className="member-avatar"
                          style={{
                            background: `linear-gradient(135deg, #667eea 0%, #764ba2 100%)`
                          }}
                        >
                          {member.username[0].toUpperCase()}
                        </div>
                        <div className="member-details">
                          <div className="member-name">{member.username}</div>
                          <div className="member-joined">Joined {formatDate(member.joined_at)}</div>
                          {member.roles && member.roles.length > 0 && (
                            <div className="member-roles">
                              {member.roles.map(role => (
                                <span 
                                  key={role.id} 
                                  className="member-role-badge"
                                  style={{ backgroundColor: role.color }}
                                >
                                  {role.name}
                                  <button 
                                    className="remove-role"
                                    onClick={() => handleRemoveRole(member.user_id, role.id)}
                                  >
                                    ✕
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <button 
                        className="btn-manage-roles"
                        onClick={() => setSelectedMember(selectedMember === member.user_id ? null : member.user_id)}
                      >
                        Manage Roles
                      </button>
                      {selectedMember === member.user_id && (
                        <div className="role-assignment-dropdown">
                          <div className="role-dropdown-header">Assign Roles</div>
                          {roles.length === 0 ? (
                            <div className="role-empty-state">No roles available. Create roles first.</div>
                          ) : (
                            roles.map(role => {
                              const hasRole = member.roles?.some(r => r.id === role.id);
                              return (
                                <div 
                                  key={role.id} 
                                  className="role-option"
                                  onClick={() => {
                                    if (hasRole) {
                                      handleRemoveRole(member.user_id, role.id);
                                    } else {
                                      handleAssignRole(member.user_id, role.id);
                                    }
                                  }}
                                >
                                  <input type="checkbox" checked={hasRole} readOnly />
                                  <span style={{ color: role.color }}>{role.name}</span>
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'roles' && (
            <div className="settings-section">
              <h2>Roles</h2>
              <button className="btn-create-role" onClick={() => setShowCreateRole(true)}>
                + Create Role
              </button>

              {showCreateRole && (
                <div className="role-editor">
                  <h3>Create New Role</h3>
                  <div className="role-form">
                    <div className="form-group">
                      <label>Role Name</label>
                      <input
                        type="text"
                        value={newRole.name}
                        onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
                        placeholder="Enter role name"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Color</label>
                      <div className="color-presets">
                        {COLOR_PRESETS.map(color => (
                          <div
                            key={color}
                            className={`color-preset ${newRole.color === color ? 'selected' : ''}`}
                            style={{ background: color }}
                            onClick={() => setNewRole({ ...newRole, color, gradient: null })}
                            title={color}
                          />
                        ))}
                      </div>
                      <div className="custom-color-picker">
                        <label>Custom Color:</label>
                        <input
                          type="color"
                          value={newRole.color}
                          onChange={(e) => setNewRole({ ...newRole, color: e.target.value, gradient: null })}
                        />
                        <span style={{ color: newRole.color }}>{newRole.color}</span>
                      </div>
                    </div>
                    
                    <div className="form-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={!!newRole.gradient}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNewRole({ ...newRole, gradient: `linear-gradient(90deg, ${newRole.color}, #FFFFFF)` });
                            } else {
                              setNewRole({ ...newRole, gradient: null });
                            }
                          }}
                        />
                        Enable Gradient
                      </label>
                      {newRole.gradient && (
                        <div className="gradient-editor">
                          <div className="gradient-preview" style={{ background: newRole.gradient }}></div>
                          <div className="gradient-controls">
                            <label>Secondary Color:</label>
                            <input
                              type="color"
                              onChange={(e) => setNewRole({ 
                                ...newRole, 
                                gradient: `linear-gradient(90deg, ${newRole.color}, ${e.target.value})` 
                              })}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="form-group">
                      <label className="holographic-toggle">
                        <input
                          type="checkbox"
                          checked={newRole.holographic}
                          onChange={(e) => setNewRole({ ...newRole, holographic: e.target.checked })}
                        />
                        <span className="holographic-label">
                          Enable Holographic Effect ✨
                        </span>
                        {newRole.holographic && (
                          <div className="holographic-preview" style={{ 
                            background: newRole.gradient || newRole.color,
                            animation: 'holographic 3s linear infinite'
                          }}>
                            Preview
                          </div>
                        )}
                      </label>
                    </div>
                    
                    <div className="form-group">
                      <label>Permissions</label>
                      <div className="permissions-list">
                        {Object.entries(PERMISSIONS).map(([name, value]) => (
                          <div key={name} className="permission-item">
                            <input
                              type="checkbox"
                              checked={hasPermission(newRole.permissions, value)}
                              onChange={() => setNewRole({
                                ...newRole,
                                permissions: togglePermission(newRole.permissions, value)
                              })}
                            />
                            <label>{name.replace(/_/g, ' ')}</label>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="form-actions">
                      <button onClick={handleCreateRole} className="btn-save">Create</button>
                      <button onClick={() => {
                        setShowCreateRole(false);
                        setNewRole({ name: '', color: '#5865F2', permissions: 0, gradient: null, holographic: false });
                      }} className="btn-cancel">Cancel</button>
                    </div>
                  </div>
                </div>
              )}

              {loading ? (
                <div className="loading">Loading roles...</div>
              ) : (
                <div className="roles-list">
                  {roles.map(role => (
                    <div key={role.id} className="role-item">
                      {editingRole === role.id ? (
                        <div className="role-editor">
                          <div className="role-form">
                            <div className="form-group">
                              <label>Role Name</label>
                              <input
                                type="text"
                                value={role.name}
                                onChange={(e) => setRoles(roles.map(r => 
                                  r.id === role.id ? { ...r, name: e.target.value } : r
                                ))}
                              />
                            </div>
                            
                            <div className="form-group">
                              <label>Color</label>
                              <div className="color-presets">
                                {COLOR_PRESETS.map((presetColor) => (
                                  <div
                                    key={presetColor}
                                    className={`color-preset ${role.color === presetColor ? 'selected' : ''}`}
                                    style={{ backgroundColor: presetColor }}
                                    onClick={() => setRoles(roles.map(r => 
                                      r.id === role.id ? { ...r, color: presetColor } : r
                                    ))}
                                  />
                                ))}
                              </div>
                              <div className="custom-color-picker">
                                <input
                                  type="color"
                                  value={role.color}
                                  onChange={(e) => setRoles(roles.map(r => 
                                    r.id === role.id ? { ...r, color: e.target.value } : r
                                  ))}
                                />
                                <input
                                  type="text"
                                  value={role.color}
                                  onChange={(e) => setRoles(roles.map(r => 
                                    r.id === role.id ? { ...r, color: e.target.value } : r
                                  ))}
                                  placeholder="#5865F2"
                                />
                              </div>
                            </div>

                            <div className="form-group">
                              <label className="gradient-label">
                                <input
                                  type="checkbox"
                                  checked={!!role.gradient}
                                  onChange={(e) => setRoles(roles.map(r => 
                                    r.id === role.id 
                                      ? { ...r, gradient: e.target.checked ? `linear-gradient(90deg, ${r.color}, #57F287)` : null }
                                      : r
                                  ))}
                                />
                                Enable Gradient
                              </label>
                              
                              {role.gradient && (
                                <div className="gradient-editor">
                                  <div 
                                    className="gradient-preview"
                                    style={{ background: role.gradient }}
                                  >
                                    <span>Preview</span>
                                  </div>
                                  <div className="gradient-controls">
                                    <label>Secondary Color:</label>
                                    <input
                                      type="color"
                                      value={role.gradient?.match(/#[0-9A-F]{6}/gi)?.[1] || '#57F287'}
                                      onChange={(e) => {
                                        const primaryColor = role.color;
                                        setRoles(roles.map(r => 
                                          r.id === role.id 
                                            ? { ...r, gradient: `linear-gradient(90deg, ${primaryColor}, ${e.target.value})` }
                                            : r
                                        ));
                                      }}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>

                            <div className="form-group">
                              <label className="holographic-label">
                                <input
                                  type="checkbox"
                                  checked={!!role.holographic}
                                  onChange={(e) => setRoles(roles.map(r => 
                                    r.id === role.id ? { ...r, holographic: e.target.checked } : r
                                  ))}
                                />
                                Enable Holographic Effect
                              </label>
                              
                              {role.holographic && (
                                <div className="holographic-preview">
                                  <span style={{ color: role.color }}>Holographic Preview</span>
                                </div>
                              )}
                            </div>

                            <div className="form-group">
                              <label>Permissions</label>
                              <div className="permissions-list">
                                {Object.entries(PERMISSIONS).map(([name, value]) => (
                                  <div key={name} className="permission-item">
                                    <input
                                      type="checkbox"
                                      checked={hasPermission(role.permissions, value)}
                                      onChange={() => setRoles(roles.map(r => 
                                        r.id === role.id 
                                          ? { ...r, permissions: togglePermission(r.permissions, value) }
                                          : r
                                      ))}
                                    />
                                    <label>{name.replace(/_/g, ' ')}</label>
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div className="form-actions">
                              <button 
                                onClick={() => handleUpdateRole(role.id, {
                                  name: role.name,
                                  color: role.color,
                                  permissions: role.permissions,
                                  gradient: role.gradient,
                                  holographic: role.holographic
                                })} 
                                className="btn-save"
                              >
                                Save
                              </button>
                              <button 
                                onClick={() => {
                                  setEditingRole(null);
                                  fetchRoles(); // Reload to reset changes
                                }} 
                                className="btn-cancel"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="role-display">
                          <div className="role-info">
                            <div 
                              className="role-color" 
                              style={{ backgroundColor: role.color }}
                            ></div>
                            <span className="role-name">{role.name}</span>
                          </div>
                          <div className="role-actions">
                            <button 
                              onClick={() => setEditingRole(role.id)} 
                              className="btn-edit"
                            >
                              Edit
                            </button>
                            <button 
                              onClick={() => handleDeleteRole(role.id)} 
                              className="btn-delete"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
}

export default ServerSettings;
